<template>
  <div class="listing-page credit-app-page">
    <div class="card">
      <header class="card__header">
        <h2>Credit Dashboard</h2>
      </header>

      <div class="card__body">
        <div class="row">
          <div class="col-sm-6">
            <el-input
              v-model="filters.search"
              clearable
              placeholder="Search Names, Rep Names and Customer Codes"
              @keypress.enter="search(filters.search)"
            />
          </div>
          <div class="col-sm-3">
            <button class="btn btn-dark" @click="search(filters.search)">Search</button>
          </div>
          <div class="col-sm-3">
            <div>
              <button
                  class="btn btn-dark"
                  @click="$router.push({ name: 'credit-dashboard.applications.invite' })"
              >
                Send Application Invite
              </button>
            </div>
            <div class="m-t-1">
              <button
                  class="btn btn-dark"
                  @click="exportPendingReport"
              >
                Export Pending Report
              </button>
            </div>

          </div>
        </div>
        <div class="row">
          <div class="col-sm-3">
            <el-select
              v-model="filters.values"
              clearable
              multiple
              filterable
              allow-create
              default-first-option
              placeholder="Filters"
            >
              <el-option-group
                v-for="group in filters.groups"
                :key="group.label"
                :label="group.label"
              >
                <el-option
                  v-for="item in group.options"
                  :key="item.value"
                  :label="item.label"
                  :value="item.value"
                ></el-option>
              </el-option-group>
            </el-select>
          </div>

          <div class="col-sm-3">
            <el-select
              v-model="filters.type"
              clearable
              default-first-option
              placeholder="Filter by Stage"
            >
              <el-option
                v-for="item in filters.types"
                :key="item.value"
                :label="item.label"
                :value="item.value"
              ></el-option>
            </el-select>
          </div>
        </div>

        <div class="row">
          <div class="col-sm-3">
            <el-select
              v-model="filters.agency"
              clearable
              placeholder="Filter by Agency"
            >
              <el-option
                v-for="item in filters.agencies"
                :key="item.value"
                :label="item.label"
                :value="item.value"
              ></el-option>
            </el-select>
          </div>
        </div>
      </div>

      <div class="card__body table-responsive">
        <table style="width: 100%;">
          <thead>
            <tr class="item">
              <th v-for="(item, key) in filters.sortables" :key="key">
                <button class="btn btn-link" @click="() => sort(key)">
                  {{ item.label }}
                  <i
                    v-if="filters.sort.key === key"
                    :class="['glyphicon', {
                        'glyphicon-triangle-top': filters.sort.direction === 'asc',
                        'glyphicon-triangle-bottom': filters.sort.direction === 'desc',
                      }]"
                  ></i>
                </button>
              </th>
              <th></th>
            </tr>
          </thead>
          <tbody v-if="sortedData.length > 0">
            <tr
              v-for="item in sortedData"
              :key="`${item.stage}-${item.id}`"
              :class="`item item--${item.status}`"
            >
              <td>
                <div class="item__primary">{{ item.ccode }}</div>
                <div class="item__tertiary">{{ item.account_name }}</div>
              </td>
              <td>
                <div class="item__primary">{{ item.stage }}</div>
                <div class="item__tertiary">{{ item.status }}</div>
              </td>
              <td>{{ item.rep_name }}</td>
              <td>{{ item.country }}</td>
              <td>{{ item.created_at.toLocaleDateString('en-CA') }}</td>
              <td>{{ item.actioned_at }}</td>
              <td class="text-warning">{{ item.next_step }}</td>
              <td>
                <button class="btn btn-success" @click="$router.push(item.router_to)">View</button>
              </td>
            </tr>
          </tbody>
          <tbody v-else>
            <tr v-for="n in 5" :key="n" class="item item--skeleton">
              <td></td>
              <td></td>
              <td></td>
              <td></td>
              <td></td>
              <td></td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="card__body" style="display: flex; justify-content: center;">
        <el-pagination
          background
          layout="prev, pager, next"
          :current-page.sync="pagination.current"
          :pager-count="9"
          :page-size="sortedData.length"
          :total="pagination.total"
          @current-change="paginate"
        />
      </div>
    </div>
  </div>
</template>

<script>
import omit from 'lodash/omit';
import axios, { RepositoryError } from "../../repositories";
import {notify} from "../../utils";

export default {
  name: "CreditDashboard",
  async mounted() {
    const { page, type, search, sort_dir, sort_key, status, agency } = this.$route.query;
    // resolve status
    let values = status;
    if (typeof status === "string") {
      values = [status];
    } else if (typeof status !== "object") {
      values = [];
    }

    this.pagination.current = Number(page) || 1;
    this.filters.type = type || "";
    this.filters.search = search || "";
    this.filters.agency = agency || '';
    this.filters.values = values;
    this.filters.sort = {
      key: sort_key || this.filters.sort.key,
      direction: sort_dir || this.filters.sort.direction,
    };

    await Promise.all([
      this.fetchCountries(),
      this.fetchAgencies(),
      this.paginate(this.pagination.current),
    ]);
  },
  data() {
    return {
      isLoading: false,
      accounts: [],
      applications: [],
      pagination: {
        current: 1,
        total: 0,
        per_page: 10
      },
      filters: {
        type: "",
        search: "",
        values: [],
        agency: '',
        agencies: [],
        sort: {
          key: 4,
          direction: "desc"
        },
        sortables: [
          {
            label: "Account Name",
            field: "account_name",
            accountColumn: "accountName",
            applicationColumn: "companyName"
          },
          {
            label: "Stage",
            field: "stage",
            accountColumn: "accountName",
            applicationColumn: "companyName"
          },
          {
            label: "Rep Name",
            field: "rep_name",
            accountColumn: "repName",
            applicationColumn: "repName"
          },
          {
            label: "Country",
            field: "country",
            accountColumn: "country",
            applicationColumn: "country"
          },
          {
            label: "Submitted At",
            field: "created_at",
            accountColumn: "created_at",
            applicationColumn: "created_at"
          },
          {
            label: "Actioned At",
            field: "actioned_at",
            accountColumn: "actioned_at",
            applicationColumn: "actioned_at"
          },
          {
            label: "Next Step",
            field: "next_step",
            accountColumn: "next_step",
            applicationColumn: "next_step"
          }
        ],
        groups: [
          {
            label: "Status",
            options: [
              { label: "Approved", value: "status:approved" },
              { label: "Declined", value: "status:declined" },
              { label: "Pending", value: "status:pending" }
            ]
          }
        ],
        types: [
          { label: "Accounts", value: "accounts" },
          { label: "Applications", value: "applications" }
        ]
      },
      fields: {
        countries: []
      }
    };
  },
  computed: {
    sortedData() {
      const accounts = this.accounts.map(a => ({
        id: a.id,
        stage: "account",
        status: a.status,
        rep_name: a.repName,
        account_name: a.accountName,
        ccode: "-",
        country: this.countryFromCode(a.country),
        created_at: new Date(a.created_at),
        actioned_at: a.actioned_at ? new Date(a.actioned_at).toLocaleDateString('en-CA') : 'Pending',
        next_step: a.actioned_at ? ((a.application || a.status === 'declined') ? '' : 'Awaiting Customer Application') : (a.status === 'pending' ? 'Review Account Request' : ''),
        router_to: {
          name: `credit-dashboard.accounts.review`,
          params: { id: a.id }
        }
      }));

      const applications = this.applications.map(a => ({
        id: a.id,
        stage: "application",
        status: a.status,
        rep_name: a.repName,
        account_name: a.companyName,
        ccode: a.ccode,
        country: this.countryFromCode(a.country),
        created_at: new Date(a.created_at),
        actioned_at: a.actioned_at ? new Date(a.actioned_at).toLocaleDateString('en-CA') : 'Pending',
        next_step: (a.actioned_at || a.status === 'approved') ? '' : 'Review Application',
        router_to: {
          name: `credit-dashboard.applications.review`,
          params: { id: a.id }
        }
      }));

      const direction = this.filters.sort.direction;
      const field = this.filters.sortables[this.filters.sort.key].field;
      const data = [...accounts, ...applications];

      if (field === "created_at") {
        switch (direction) {
          case "asc":
            return data.sort((a, b) => a.created_at - b.created_at);
          case "desc":
            return data.sort((a, b) => b.created_at - a.created_at);
          default:
            throw Error(`unknown sort direction: ${direction}`);
        }
      }

      switch (direction) {
        case "asc":
          return data.sort((a, b) => {
            if (a[field] < b[field]) {
              return -1;
            }

            if (b[field] < a[field]) {
              return 1;
            }

            return 0;
          });
        case "desc":
          return data.sort((a, b) => {
            if (a[field] > b[field]) {
              return -1;
            }

            if (b[field] > a[field]) {
              return 1;
            }

            return 0;
          });
        default:
          throw Error(`unknown sort direction: ${direction}`);
      }
    }
  },
  methods: {
    async fetchAccounts(page) {
      return await axios.get("/credit-dashboard/accounts", {
        params: {
          page,
          limit: this.pagination.per_page,
          filters: this.resolveFilters(),
          sort_by: this.filters.sortables[this.filters.sort.key].accountColumn,
          sort_dir: this.filters.sort.direction,
          fields: [
            "id",
            "status",
            "repName",
            "accountName",
            "country",
            "created_at",
            "actioned_at"
          ]
        }
      });
    },
    async fetchApplications(page) {
      return await axios.get("/credit-dashboard/applications", {
        params: {
          page,
          limit: this.pagination.per_page,
          filters: this.resolveFilters(),
          sort_by: this.filters.sortables[this.filters.sort.key]
            .applicationColumn,
          sort_dir: this.filters.sort.direction,
          fields: [
            "id",
            "status",
            "repName",
            "companyName",
            "ccode",
            "country",
            "created_at",
            "actioned_at"
          ]
        }
      });
    },
    async paginate(page) {
      this.clear();
      this.$router.push({ query: { ...this.$route.query, page } }).catch(() => {});
      if (this.filters.type) {
        return this.filterType(this.filters.type);
      }

      try {
        const accounts = await this.fetchAccounts(page);
        const applications = await this.fetchApplications(page);

        this.pagination.current = page;
        this.accounts = accounts.data.data;
        this.applications = applications.data.data;
        this.pagination.total = accounts.data.total + applications.data.total;
      } catch (err) {
        if (err instanceof RepositoryError) {
          return;
        }

        throw err;
      }
    },
    async filterType(type) {
      this.clear();

      let res = null;
      let total = 0;

      try {
        switch (type) {
          case "accounts":
            await this.$router.push({ query: {...this.$route.query, type} }).catch(() => {});
            res = await this.fetchAccounts(this.pagination.current);
            total = res.data.total;
            this.accounts = res.data.data;
            break;
          case "applications":
            await this.$router.push({ query: {...this.$route.query, type} }).catch(() => {});
            res = await this.fetchApplications(this.pagination.current);
            total = res.data.total;
            this.applications = res.data.data;
            break;
          default:
            await this.$router.push({ query: omit(this.$route.query, ['type'])}).catch(() => {});
            await this.paginate(this.pagination.current);
            return;
        }
      } catch (err) {
        if (err instanceof RepositoryError) {
          return;
        }

        throw err;
      }

      this.pagination.total = total;
    },
    async fetchCountries() {
      try {
        const response = await axios.get(
          "/credit-dashboard/metadata/countries"
        );

        this.fields.countries = response.data;
      } catch (err) {
        if (err instanceof RepositoryError) {
          return;
        }
        
        throw err;
      }
    },
    async fetchAgencies() {
      const countries = ['ca', 'usa', 'other'];
      let agencies = [];
      
      for (const c in countries) {
        const response = await axios.get(`/credit-dashboard/metadata/countries/${countries[c]}/agencies`);
        agencies = agencies.concat(response.data);
      }

      this.filters.agencies = agencies;
    },
    async search(query) {
      if (String(query).trim().length === 0) {
        this.$router.push({ query: omit(this.$route.query, ['search'])}).catch(() => {});
        await this.paginate(this.pagination.current);
        return;
      }

      this.clear();

      await this.$router.push({ query: {...this.$route.query, search: query} }).catch(() => {});
      try {
        const accounts = await axios.get(
          "/credit-dashboard/accounts/search",
          {
            params: { query }
          }
        );

        const applications = await axios.get(
          "/credit-dashboard/applications/search",
          {
            params: { query }
          }
        );

        this.accounts = accounts.data.data;
        this.applications = applications.data.data;
        this.pagination.total = accounts.data.total + applications.data.total;
      } catch (err) {
        if (err instanceof RepositoryError) {
          return;
        }
        
        throw err;
      }
    },
    async exportPendingReport() {
      try {
        await axios.get("/credit-dashboard/applications/pending-report", {responseType: "blob"}).then(response => {
          const csvData = response.data;
          let headerLine = response.headers['content-disposition'];
          let startFileNameIndex = headerLine.indexOf('filename=') + 9;
          let filename = headerLine.substring(startFileNameIndex, headerLine.length);
          if (navigator.msSaveBlob) {
            navigator.msSaveBlob(csvData, filename);
            return;
          }
          let link = document.createElement("a");
          link.href = window.URL.createObjectURL(csvData);
          link.setAttribute("download", filename);
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        });

      } catch (err) {
        if (err instanceof RepositoryError) {
          return;
        }

        if (err.response && err.response.data.errors) {
          notify(err.response.data.errors, 'error');
          return;
        }

        throw err;
      }
    },
    countryFromCode(code) {
      const country = this.fields.countries.find(val => val.value === code);
      if (!country) {
        return code;
      }

      return country.label;
    },
    clear() {
      this.accounts = [];
      this.applications = [];
    },
    resolveDirection(direction) {
      switch (direction) {
        case "asc":
          return "desc";
        case "desc":
          return "asc";
        default:
          return "asc";
      }
    },
    sort(key) {
      if (this.filters.sort.key === key) {
        this.filters.sort.direction = this.resolveDirection(
          this.filters.sort.direction
        );

        this.$router.push({query: {
          ...this.$route.query,
          sort_dir: this.filters.sort.direction,
        }}).catch(() => {});
        return;
      }

      this.$router.push({query: {
        ...this.$route.query,
        sort_key: key,
        sort_dir: this.filters.sort.direction,
      }}).catch(() => {});

      this.filters.sort.key = key;
      this.filters.sort.direction = this.resolveDirection();
    },
    resolveFilters() {
      const filters = [...this.filters.values];

      if (this.filters.agency.toString().trim() !== '') {
        filters.push(`agency:${this.filters.agency}`);
      }

      return filters;
    },
  },
  watch: {
    "filters.type": function(type) {
      this.filterType(type);
    },
    "filters.agency": function(value) {
      if (String(value).length === 0) {
        this.$router.push({ query: omit(this.$route.query, ['agency'])}).catch(() => {});
        return this.paginate(this.pagination.current);
      }

      this.$router.push({ query: {...this.$route.query, agency: value}}).catch(() => {});
      this.paginate(this.pagination.current);
    },
    "filters.values": function(values) {
      if (values.length === 0) {
        this.$router.push({ query: omit(this.$route.query, ['status'])}).catch(() => {});
        return this.paginate(this.pagination.current);
      }

      this.$router.push({ query: {...this.$route.query, status: values}}).catch(() => {});
      this.paginate(this.pagination.current);
    },
    "filters.search": function(query) {
      if (String(query).trim().length !== 0) {
        return;
      }

      this.$router.push({ query: omit(this.$route.query, ['search'])}).catch(() => {});
      this.paginate(this.pagination.current);
    },
    "filters.sort.key": function() {
      this.paginate(this.pagination.current);
    },
    "filters.sort.direction": function() {
      this.paginate(this.pagination.current);
    }
  }
};
</script>
